import { useEffect, useState } from 'react'
import api from '../services/api'

// Admissão / Demissão do funcionário (TMOTCA2 do ERP, frota.motca2).
// Admissão já gravada é a chave da linha e não muda; linha não pode ser excluída (igual ao ERP).
// Motivo da demissão (tabela 19 do eSocial) e observação de até 500 caracteres vêm da migration 057; sem ela a tela fica como era (observação de 50, sem motivo).

interface Linha { adm: string; dem: string; obs: string; usu: string; mot?: string; nova?: boolean }
interface Motivo { codigo: string; descricao: string }

export default function FuncionarioAdmissaoModal({ motcod, motnome, onClose, onSalvo }: {
  motcod: string; motnome: string; onClose: () => void; onSalvo?: () => void
}) {
  const [linhas, setLinhas] = useState<Linha[] | null>(null)
  const [motivos, setMotivos] = useState<Motivo[]>([])
  const [temMotivo, setTemMotivo] = useState(false)
  const [obsMax, setObsMax] = useState(50)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/admissoes`)
    setLinhas(r.data)
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar as admissões do funcionário.'))
    api.get('/funcionarios/motivos-desligamento')
      .then(r => { setMotivos(r.data.motivos ?? []); setTemMotivo(!!r.data.temMotivo); setObsMax(Number(r.data.obsMax) || 50) })
      .catch(() => { /* sem a lista, a tela segue como antes */ })
  }, [motcod])

  const set = (i: number, campo: keyof Linha, valor: string) =>
    setLinhas(ls => ls!.map((l, j) => (j === i ? { ...l, [campo]: valor, ...(campo === 'dem' && !valor ? { mot: '' } : {}) } : l)))

  async function salvar() {
    setErro(''); setAviso('')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/admissoes`, { linhas })
      await carregar()
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Admissão/Demissão salva.')
      if (!r.data?.semAlteracao) onSalvo?.()
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar admissão/demissão.')
    } finally {
      setSalvando(false)
    }
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.45rem 0.6rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.3rem 0.4rem', verticalAlign: 'top' }
  const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '0.35rem 0.5rem', fontSize: '0.82rem' }
  const colunas = temMotivo ? 5 : 4

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: temMotivo ? 1100 : 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Admissão / Demissão — {motcod} · {motnome}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTCA2</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!linhas ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 150 }}>Admissão</th>
                    <th style={{ ...th, width: 150 }}>Demissão</th>
                    {temMotivo && <th style={{ ...th, width: 300 }}>Motivo da demissão (eSocial)</th>}
                    <th style={th}>Observação</th>
                    <th style={{ ...th, width: 100 }}>Usuário</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.length === 0 && (
                    <tr><td colSpan={colunas} style={{ textAlign: 'center', color: '#6b7280', padding: '1.2rem' }}>Nenhuma admissão lançada.</td></tr>
                  )}
                  {linhas.map((l, i) => {
                    const codigoFora = !!l.mot && !motivos.some(m => m.codigo === l.mot) // código gravado que saiu da lista de ativos
                    return (
                      <tr key={l.nova ? `nova-${i}` : l.adm}>
                        <td style={td}>
                          <input type="date" value={l.adm} disabled={!l.nova} style={inp}
                            title={l.nova ? undefined : 'Admissão já gravada não pode ser alterada'}
                            onChange={e => set(i, 'adm', e.target.value)} />
                        </td>
                        <td style={td}>
                          <input type="date" value={l.dem} style={inp} onChange={e => set(i, 'dem', e.target.value)} />
                        </td>
                        {temMotivo && (
                          <td style={td}>
                            <select value={l.mot ?? ''} disabled={!l.dem} style={inp} onChange={e => set(i, 'mot', e.target.value)}
                              title={l.dem ? undefined : 'Informe a data da demissão para escolher o motivo'}>
                              <option value="">{l.dem ? 'Selecione o motivo...' : '—'}</option>
                              {codigoFora && <option value={l.mot}>{l.mot}</option>}
                              {motivos.map(m => <option key={m.codigo} value={m.codigo}>{m.codigo} - {m.descricao}</option>)}
                            </select>
                          </td>
                        )}
                        <td style={td}>
                          {obsMax > 50 ? (
                            <>
                              <textarea value={l.obs} maxLength={obsMax} rows={2} style={{ ...inp, resize: 'vertical' }} onChange={e => set(i, 'obs', e.target.value.toUpperCase())} />
                              <div style={{ textAlign: 'right', fontSize: '0.68rem', color: l.obs.length >= obsMax ? '#dc2626' : '#94a3b8' }}>{l.obs.length}/{obsMax}</div>
                            </>
                          ) : (
                            <input value={l.obs} maxLength={obsMax} style={inp} onChange={e => set(i, 'obs', e.target.value.toUpperCase())} />
                          )}
                        </td>
                        <td style={{ ...td, fontSize: '0.8rem', color: '#6b7280' }}>{l.usu || '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }}
                onClick={() => setLinhas(ls => [...ls!, { adm: '', dem: '', obs: '', usu: '', mot: '', nova: true }])}>
                + Nova admissão
              </button>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.75rem 0 0' }}>
                A última admissão define a situação do funcionário: com demissão fica Inativo, sem demissão fica Ativo.
                {temMotivo && ' O motivo é obrigatório ao informar ou alterar a data de demissão.'}
              </p>
            </>
          )}

          {linhas && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !linhas}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
